import { db, type OutboxRecord } from "./dexie-db";
import { apiFetch, NetworkError, APIError } from "./api-client";

/**
 * Offline-outbox. Mutations gehen optimistically in den local-state
 * (TanStack-Query setMutationDefaults), DAUERND laufen sie über enqueue()
 * UND werden direkt versucht — bei NetworkError bleiben sie liegen, beim
 * 'online'-event flush'en wir.
 *
 * Idempotency: jede mutation kriegt ein client_uuid; server gibt 200
 * statt 201 zurück wenn die row schon existiert (siehe entries_crud.go
 * CreateEntryV2). → retry-safe beliebig oft.
 *
 * Backoff: 1s/2s/5s/30s/5min/abandon nach 10 attempts. Bei abandon:
 * 'hitzeprotokoll:outbox-abandoned'-event mit der record-id.
 */

const BACKOFF_MS = [1000, 2000, 5000, 30000, 60_000, 120_000, 300_000];
const MAX_ATTEMPTS = 10;

export interface EnqueueInput {
  method: "POST" | "PATCH" | "DELETE";
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
  clientUuid?: string;
}

export async function enqueueMutation(input: EnqueueInput): Promise<number> {
  const id = await db.outbox.add({
    method: input.method,
    url: input.url,
    body: input.body !== undefined ? JSON.stringify(input.body) : undefined,
    headers: input.headers,
    createdAt: Date.now(),
    attempts: 0,
    status: "pending",
    clientUuid: input.clientUuid,
  });
  return typeof id === "number" ? id : Number(id);
}

interface SendResult {
  ok: boolean;
  retriable: boolean;
  status?: number;
  errorCode?: string;
}

async function sendOne(record: OutboxRecord): Promise<SendResult> {
  try {
    const body = record.body ? JSON.parse(record.body) : undefined;
    await apiFetch(record.url, {
      method: record.method,
      body,
      headers: record.headers,
    });
    return { ok: true, retriable: false };
  } catch (err) {
    if (err instanceof NetworkError) {
      return { ok: false, retriable: true };
    }
    if (err instanceof APIError) {
      // 4xx (außer 408/429) = bug im client, nicht retry-bar. 5xx = retry.
      const retriable = err.status === 408 || err.status === 429 || err.status >= 500;
      return { ok: false, retriable, status: err.status, errorCode: err.code };
    }
    return { ok: false, retriable: false, errorCode: "unknown" };
  }
}

export async function flushOutbox(): Promise<{ sent: number; failed: number }> {
  const pending = await db.outbox.where("status").anyOf("pending", "failed").toArray();
  let sent = 0;
  let failed = 0;
  for (const record of pending) {
    if (record.attempts >= MAX_ATTEMPTS) {
      window.dispatchEvent(
        new CustomEvent("hitzeprotokoll:outbox-abandoned", { detail: { id: record.id } })
      );
      continue;
    }
    const nextAttempt = record.attempts + 1;
    const lastBackoff = BACKOFF_MS[Math.min(record.attempts, BACKOFF_MS.length - 1)];
    if (record.lastAttemptAt && Date.now() - record.lastAttemptAt < (lastBackoff ?? 0)) {
      continue;
    }
    await db.outbox.update(record.id!, {
      status: "in_flight",
      lastAttemptAt: Date.now(),
      attempts: nextAttempt,
    });

    const result = await sendOne(record);
    if (result.ok) {
      await db.outbox.delete(record.id!);
      sent++;
      continue;
    }
    if (!result.retriable) {
      await db.outbox.update(record.id!, {
        status: "failed",
        errorMessage: result.errorCode,
      });
      failed++;
      continue;
    }
    await db.outbox.update(record.id!, {
      status: "pending",
      errorMessage: result.errorCode,
    });
  }
  return { sent, failed };
}

/**
 * Lifecycle-binding: kicks off whenever we see online/visibility events.
 * Auch zeitgesteuert (60s) damit zwischen-tab-handoffs nicht hängen
 * bleiben.
 */
export function startOutboxLoop(): () => void {
  const handler = () => {
    if (navigator.onLine !== false) {
      void flushOutbox();
    }
  };
  window.addEventListener("online", handler);
  document.addEventListener("visibilitychange", handler);
  const interval = window.setInterval(handler, 60_000);
  // initial kick
  handler();
  return () => {
    window.removeEventListener("online", handler);
    document.removeEventListener("visibilitychange", handler);
    window.clearInterval(interval);
  };
}

export async function pendingOutboxCount(): Promise<number> {
  return await db.outbox.where("status").anyOf("pending", "failed").count();
}
